'use client';

import { insertLeadForPageOfPageCollection } from '@/data/leads';
import { useState } from 'react';
import { ParticipateInLotteryForm } from './ParticipateInLotteryForm';
import { PageCollectionConfigLanguage } from '@creator-suite/page-types';
import { TypeEnumIncentiveType } from '@creator-suite/enums';
import { getPageCollectionByDomainSubdomainAndPath } from '@/data/page-collections';
import { InsertLeadToPageFormSchemaType } from '@/data/leads-dto';
import { PageHeader } from './PageHeader';
import { LotteryAnimatedCard } from './LotteryAnimatedCard';
import { CheckLotteryEntryStatusForm } from './CheckLotteryEntryStatusForm';
import { CheckLotteryStatusFormType } from '@/data/lotteries-dto';
import { checkLotteryStatus } from '@/data/lotteries';
import { Background } from '@/components/atoms/background-image';
import { HaloContainer } from './HaloContainer';

type CheckLotteryState = {
  status: 'initial' | 'loading' | 'success' | 'error';
  error?: string;
  result?: {
    status: 'not_found' | 'lottery_not_found' | 'pending' | 'winner' | 'loser';
    callToAction?: {
      label: string;
      source: string;
    };
  };
};

export interface IncentiveLotteryProps {
  pageCollection: Omit<
    Awaited<ReturnType<typeof getPageCollectionByDomainSubdomainAndPath>>,
    'page' | 'incentive' | 'lottery'
  >;

  page: Awaited<
    ReturnType<typeof getPageCollectionByDomainSubdomainAndPath>
  >['page'];

  lottery: NonNullable<
    Awaited<
      ReturnType<typeof getPageCollectionByDomainSubdomainAndPath>
    >['lottery']
  >;

  organizationLegalName: string;
  accountDisplayName: string;
  incentiveType: TypeEnumIncentiveType;

  backgroundUrl: string;
  backgroundColor: string | null;
  backgroundShadow: boolean | null;

  slug: string;
  host: string;
  path: string;
  domain: string;
  language: PageCollectionConfigLanguage;
  privacyPolicyUrl: string;
  countryCode?: string;
}

const handleFormError = (error: unknown) => {
  console.error(error);
};

export const IncentiveLottery: React.FC<IncentiveLotteryProps> = (props) => {
  const {
    slug,
    language,
    lottery,
    pageCollection,
    page,
    path,
    host,
    domain,
    accountDisplayName,
    backgroundUrl,
    backgroundColor,
    backgroundShadow,
    countryCode,
  } = props;

  const today = new Date();

  const lotteryState =
    lottery.startAt >= today
      ? 'not-started'
      : lottery.winners
        ? 'winners-selected'
        : lottery.endAt < today
          ? 'no-winners-yet'
          : 'active';

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [checkState, setCheckState] = useState<CheckLotteryState>({
    status: 'initial',
  });

  const handleCheckFormSubmit = async (data: CheckLotteryStatusFormType) => {
    document.querySelector('.toShow')?.classList.add('animate-hide');
    try {
      setCheckState({ status: 'loading' });
      const result = await checkLotteryStatus(lottery.id, data);
      setCheckState({ status: 'success', result });
      document.querySelector('.toFlip')?.classList.add('animate-hflip');
      document.querySelector('.toShow')?.classList.add('animate-show');
    } catch (error) {
      console.error('Error checking lottery status:', error);
      setCheckState({
        status: 'error',
        error:
          'An error occurred while checking your lottery status. Please try again.',
      });
    }
  };

  const [state, setState] = useState<
    'initial' | 'loading' | 'success' | 'error'
  >('initial');
  const handleFormSubmit = async (data: InsertLeadToPageFormSchemaType) => {
    setState('loading');

    let result;

    try {
      result = await insertLeadForPageOfPageCollection({
        pageCollectionSlug: slug,
        path,
        data: data,
      });
    } catch (error: unknown) {
      // FIXME: Add Sentry handling
      console.error(error);
      setState('error');
      setErrorMessage(
        'Leider ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      );
    }

    if (result?.validationErrors) {
      console.error('Validation errors:', result.validationErrors);
      setState('error');
      setErrorMessage('Bitte überprüfe deine Eingaben und versuche es erneut.');
      throw new Error('Validation failed');
    }

    if (result?.serverError) {
      const error = result.serverError as { name?: string };
      console.error(`An unhandled error occurred.`);
      console.error(error);
      throw error;
    }

    setState('success');
  };

  const backgroundImageAlt = '';

  const BackgroundNode = backgroundUrl ? (
    <Background
      src={backgroundUrl}
      alt={backgroundImageAlt}
      tintMode={backgroundShadow ? 'strong' : 'partial'}
    />
  ) : backgroundColor ? (
    <Background
      backgroundColor={backgroundColor}
      tintMode={backgroundShadow ? 'strong' : 'partial'}
    />
  ) : null;

  return (
    <HaloContainer
      backgroundColor={backgroundUrl ? null : backgroundColor}
      className="toFlip"
    >
      <LotteryAnimatedCard className="LotteryAnimatedCard relative z-10 min-h-[600px] animate-pulse">
        {BackgroundNode}
        <div className="toShow relative flex h-[600px] flex-col gap-8">
          <PageHeader
            host={host}
            domain={domain}
            primaryColor={pageCollection.config.primaryColor}
            headerText={pageCollection.config.headerText}
            logoImageUrl={pageCollection.config.logoUrl}
            logoWidthPx={pageCollection.config.logoWidthPx ?? 0}
            fontFamily={pageCollection.config.fontFamily}
            title={page.title}
            mode={lotteryState === 'winners-selected' ? 'logo-only' : 'default'}
          >
            {page.description}
          </PageHeader>
          <div className="flex grow justify-center">
            {lotteryState === 'active' ? (
              <ParticipateInLotteryForm
                language={language}
                accountDisplayName={accountDisplayName}
                handleSubmit={handleFormSubmit}
                onFormError={handleFormError}
                buttonRadiusClassName={
                  pageCollection.config.buttonRadiusClassName
                }
                inputRadiusClassName={
                  pageCollection.config.inputRadiusClassName
                }
                cardRadiusClassName={pageCollection.config.cardRadiusClassName}
                buttonText={
                  language === 'de'
                    ? 'Am Gewinnspiel teilnehmen'
                    : 'Join the lottery'
                }
                fontFamily={pageCollection.config.fontFamily}
                fieldset={page.data.fieldset}
                error={errorMessage}
                termsUrl={lottery.termsUrl || ''}
                state={state}
                privacyPolicyUrl={`https://${domain}/privacy-policy`}
                backgroundUrl={backgroundUrl}
                backgroundColor={backgroundColor}
                countryCode={countryCode}
              />
            ) : lotteryState === 'winners-selected' ? (
              <CheckLotteryEntryStatusForm
                language={language}
                handleSubmit={handleCheckFormSubmit}
                buttonRadiusClassName={
                  pageCollection.config.buttonRadiusClassName
                }
                inputRadiusClassName={
                  pageCollection.config.inputRadiusClassName
                }
                buttonText={
                  language === 'de' ? 'Status prüfen' : 'Check status'
                }
                fontFamily={pageCollection.config.fontFamily}
                error={checkState.error}
                state={checkState.status}
                result={checkState.result}
                backgroundColor={props.backgroundColor}
                backgroundUrl={props.backgroundUrl}
                countryCode={countryCode}
              />
            ) : lotteryState === 'not-started' ? (
              <div className="flex flex-col justify-end">
                <div className="text-center text-sm text-white/50">
                  Das Gewinnspiel hat noch nicht begonnen. Schaue am{' '}
                  {lottery.startAt.toLocaleString()} wieder vorbei.
                </div>
              </div>
            ) : lotteryState === 'no-winners-yet' ? (
              <div className="flex h-full flex-col justify-end">
                <div className="text-center text-sm text-white/50">
                  Das Gewinnspiel ist beendet. Es stehen noch keine Gewinner
                  fest.
                </div>
              </div>
            ) : (
              <p>Unknown lottery state.</p>
            )}
          </div>
        </div>
      </LotteryAnimatedCard>
    </HaloContainer>
  );
};
